import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick'; // Import React Slick
import { Helmet } from 'react-helmet-async'; // Import Helmet

// Import PNG versions (as before)
import processesImagePng from '../assets/processes.png';
import reportingImagePng from '../assets/reporting.png';
import hubImagePng from '../assets/operation1.png';
import appImagePng from '../assets/app.png';

// Import WebP versions
import processesImageWebp from '../assets/processes.webp';
import reportingImageWebp from '../assets/reporting.webp';
import hubImageWebp from '../assets/operation1.webp';
import appImageWebp from '../assets/app.webp';

import companyLogo1 from '../assets/britishgas.png'; // Replace with actual logo paths
import companyLogo2 from '../assets/nationalgrid.png';
import companyLogo3 from '../assets/npower.png';
import companyLogo4 from '../assets/centrica.png';
import companyLogo5 from '../assets/talktalk.png';
import companyLogo6 from '../assets/o2.png';
import companyLogo7 from '../assets/carphone.png';
import companyLogo8 from '../assets/mns.png';
import companyLogo9 from '../assets/homeoffice.png';
import companyLogo10 from '../assets/bae.png';
import companyLogo11 from '../assets/gsk.png';
import companyLogo12 from '../assets/sodexo.png';
import companyLogo13 from '../assets/gripple.png';
import ContactForm from '../components/ContactForm';
import Testimonials from '../components/Testimonials'; // <-- IMPORT HERE
import techLogosPng from '../assets/logos.png'; // Rename for clarity
import techLogosWebp from '../assets/logos.webp'; // Import the WebP version
import { caseStudies } from '../data/caseStudiesData'; // Import case studies data

const HomePage: React.FC = () => {
  const carouselSettings = {
    dots: false,
    infinite: true,
    speed: 2000, // Duration of the slide transition
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000, // Set to match or be slightly > speed
    pauseOnHover: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 560,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const cardsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = cardsContainerRef.current?.querySelectorAll('.card');
    if (!cards) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.2 }
    );

    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  // Find the relevant case study ID for each service type
  const processMappingStudyId = caseStudies.find(cs => cs.relatedService === 'process-mapping')?.id;
  const customReportingStudyId = caseStudies.find(cs => cs.relatedService === 'custom-reporting')?.id;
  const operationsLaunchStudyId = caseStudies.find(cs => cs.relatedService === 'operations-launch')?.id;
  const techCreationStudyId = caseStudies.find(cs => cs.relatedService === 'tech-creation')?.id;

  return (
    <div>
      <Helmet>
        <title>IntelPro | Business Improvement & Process Optimisation Specialists</title>
        {/* You can also add page-specific meta descriptions here if needed */}
        <meta name="description" content="IntelPro helps UK businesses streamline operations, optimise processes with tools like Power BI & SQL, and deliver measurable results. Contact us for a consultation." />
      </Helmet>
      <section className="jumbo-section">
        <div className="jumbo-content">
          <picture>
            {/* Use absolute paths starting with '/' */}
            <source srcSet="/jumbo-image.webp" type="image/webp" />
            <img
              src="/jumbo-image.png" // Fallback src
              alt="Business Insights"
              className="jumbo-image"
            />
          </picture>
          <div className="jumbo-text">
            <h1>Unlock Operational Efficiency</h1>
            <h3>Get Clarity | Deliver Results</h3>
            <p>We help organisations cut through complexity and build practical solutions at pace</p>
            {/* --- Change Link to <a> tag for in-page scroll --- */}
            <a href="#main-content" className="btn btn-outline-white"> {/* CHANGED CLASS HERE */}
              Find Out More
            </a>
            {/* --- End Button --- */}
          </div>
        </div>
      </section>
      <div className="container">
        <header className="header">
          <Slider {...carouselSettings} className="carousel">
            <img src={companyLogo1} alt="Company 1 Logo" />
            <img src={companyLogo2} alt="Company 2 Logo" />
            <img src={companyLogo3} alt="Company 3 Logo" />
            <img src={companyLogo4} alt="Company 4 Logo" />
            <img src={companyLogo5} alt="Company 5 Logo" />
            <img src={companyLogo6} alt="Company 6 Logo" />
            <img src={companyLogo7} alt="Company 7 Logo" />
            <img src={companyLogo8} alt="Company 8 Logo" />
            <img src={companyLogo9} alt="Company 9 Logo" />
            <img src={companyLogo10} alt="Company 10 Logo" />
            <img src={companyLogo11} alt="Company 11 Logo" />
            <img src={companyLogo12} alt="Company 12 Logo" />
            <img src={companyLogo13} alt="Company 13 Logo" />
          </Slider>
        </header>

        {/* Add id="main-content" to the main element */}
        <main id="main-content">
          {/* --- Introduction and Services Section --- */}
          <section id="intro-services" className="intro-services-section">
            <div className="container"> {/* Keep content centered */}
              <h1 className="welcome-heading">Times Are Changing. We're Here To Help</h1>
              <p className="welcome-text">
                With the acceleration of AI, <b>companies need high quality data and efficient processes</b> to gain a competitive edge as large-scale automation is set to become standard practice.
              </p>
              <p className="welcome-text">
                IntelPro is run by <b>talented problem solvers with over 10 years of cross-sector experience in business improvement</b> supporting some of the largest organisations in the UK. We are tech-savvy people who can <b>get into the detail required to deliver practical solutions that are fit for purpose</b>.
              </p>
              <h2 className="services-subheading">What We Can Do To Elevate Your Business</h2> {/* Changed to h2 for better hierarchy */}
              <div className="services" ref={cardsContainerRef}>
                {/* ... Service Cards ... */}
                {processMappingStudyId && (
                  <Link to={`/case-studies/${processMappingStudyId}`} className="card-link">
                    <div className="card">
                      {/* Use <picture> element */}
                      <picture>
                        <source srcSet={processesImageWebp} type="image/webp" />
                        <img
                          src={processesImagePng} // Fallback PNG
                          alt="Process Mapping"
                          loading="lazy"
                        />
                      </picture>
                      <h3>Process Mapping</h3>
                      <p>Visualise your workflows to help drive consistency & maximise efficiency.</p>
                    </div>
                  </Link>
                )}

                {customReportingStudyId && (
                  <Link to={`/case-studies/${customReportingStudyId}`} className="card-link">
                    <div className="card">
                      {/* Use <picture> element */}
                      <picture>
                        <source srcSet={reportingImageWebp} type="image/webp" />
                        <img
                          src={reportingImagePng} // Fallback PNG
                          alt="Custom Reporting"
                          loading="lazy"
                        />
                      </picture>
                      <h3>Custom Reporting</h3>
                      <p>Combine data to derive unique insights that can inform strategic decisions.</p>
                    </div>
                  </Link>
                )}

                {operationsLaunchStudyId && (
                  <Link to={`/case-studies/${operationsLaunchStudyId}`} className="card-link">
                    <div className="card">
                      {/* Use <picture> element */}
                      <picture>
                        <source srcSet={hubImageWebp} type="image/webp" />
                        <img
                          src={hubImagePng} // Fallback PNG
                          alt="Operations Launch"
                          loading="lazy"
                        />
                      </picture>
                      <h3>Enhanced Operations</h3>
                      <p>Deploy new equipment to maximise productivity & successful outcomes.</p>
                    </div>
                  </Link>
                )}

                {techCreationStudyId && (
                  <Link to={`/case-studies/${techCreationStudyId}`} className="card-link">
                    <div className="card">
                      {/* Use <picture> element */}
                      <picture>
                        <source srcSet={appImageWebp} type="image/webp" />
                        <img
                          src={appImagePng} // Fallback PNG
                          alt="Tech Creation"
                          loading="lazy"
                        />
                      </picture>
                      <h3>App / Web Development</h3>
                      <p>Design & build user-friendly tools to enable reliable process execution.</p>
                    </div>
                  </Link>
                )}
              </div>
              <h2 className="services-subheading">
                We Don't Just Make Recommendations<br />
                <span className="subheading-line-two">We Work With You To Implement Them</span>
              </h2>
              <p className="welcome-text">
                Our consultants are certified project managers and <b>experienced people managers who understand how to get the best out of cross-functional teams</b>.
                We recognise the importance of bringing people on the journey with us so <b>collaboration is at the heart of our approach</b>.
                We're passionate about breaking down complexity and communicating ideas in simple terms to <b>enable the best possible decisions</b>.
              </p>

              {/* --- Add Learn More Button --- */}
              <div className="learn-more-container">
                <Link to="/contact" className="btn btn-primary">
                  Make An Enquiry
                </Link>
              </div>
              {/* --- End Learn More Button --- */}

            </div>
          </section>
          {/* --- End Introduction and Services Section --- */}

          {/* --- How We Deliver Section --- */}
          <section id="how-we-deliver" className="how-we-deliver">
            <div className="container">
              <h2 className="welcome-heading">How We Deliver Our Service</h2>
              <p className="welcome-text welcome-text-no-padding">
                A structured approach ensures that we deliver measurable results while keeping you informed and involved every step of the way.
                Here's how we work:
              </p>
              <ul>
                <li>
                  <strong>Objective Setting:</strong> We begin by understanding your unique challenges and objectives. This phase includes a detailed discussion to align our goals with your business needs.
                </li>
                <li>
                  <strong>Audit & Recommendations (1-2 weeks):</strong> We conduct a thorough audit of your current processes and systems. This phase culminates in a comprehensive report outlining our findings and tailored recommendations.
                </li>
                <li>
                  <strong>Bespoke Project Delivery (3-6 months):</strong> Following the audit, we take ownership of a customised project plan designed to address your specific challenges. This phase includes regular checkpoints to ensure alignment and progress.
                </li>
                <li>
                  <strong>Tools & Technology:</strong> We use a wide range of external tools to deliver robust & scalable solutions. From data analytics and visualisation to custom application development, we make sure to use the right packages for the job.
                </li>
              </ul>
              <div className="tool-logos-container">
                {/* Use <picture> element */}
                <picture>
                  <source srcSet={techLogosWebp} type="image/webp" />
                  <img
                    src={techLogosPng} // Fallback PNG
                    alt="Logos of technologies used including Power BI, Python, React, SQL, Azure"
                    className="tech-logos-image"
                    loading="lazy"
                  />
                </picture>
              </div>

              {/* --- Add Case Studies Button --- */}
              <div className="learn-more-container"> {/* Reusing container class for centering */}
                <Link to="/case-studies" className="btn btn-primary"> {/* Use btn-secondary or another class */}
                  See Our Case Studies
                </Link>
              </div>
              {/* --- End Case Studies Button --- */}

            </div>
          </section>
          {/* --- End How We Deliver Section --- */}

          {/* --- Testimonials Section --- */}
          <Testimonials /> {/* <-- ADD COMPONENT HERE */}
          {/* --- End Testimonials Section --- */}

          <section id="contact-us" className="contact-us">
            <div className="container">
            <h1 className="welcome-heading">Contact Us</h1>
              <p className="welcome-text">
                Have questions or want to learn more about how we can help your business? Fill out the form below and we’ll get back to you!
              </p>
              <ContactForm />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default HomePage;